import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PlanCompare, useComparePlans } from '@/components/compare/PlanCompare';
import { PlanQuiz } from '@/components/compare/PlanQuiz';
import { PLANS, planPriceList } from '@/shared/pricing';
import { breadcrumbSchema, productSchema, useJsonLd, useSeo } from '@/shared/seo';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { P } from '@/shared/locales/pages/keys';

export const route = { path: '/compare', layout: 'public', access: 'public' };

const PATH = '/compare';
const KEYS = ['Wraith', 'Demon'];

const PICK = {
  Wraith: [P.wMost, P.wNA, P.wOne, P.wEpg],
  Demon: [P.dSports, P.dEU, P.dMulti, P.dLong],
};

export default function Compare() {
  const { t } = useT();
  const [conns, setConns] = useState(1);
  const { plans, ready } = useComparePlans(KEYS);
  const both = plans.length === 2;
  const { hash } = useLocation();

  // Links like /compare#quiz land on that section once the plans have loaded.
  useEffect(() => {
    if (!ready || !hash) return undefined;
    const timer = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    return () => clearTimeout(timer);
  }, [ready, hash]);

  useSeo({
    title: 'Wraith vs Demon: compare IPTV plans',
    description:
      'Compare the Ghost Cable Wraith and Demon IPTV plans side by side: screens, package lengths, prices per month and what each includes.',
    path: PATH,
  });

  useJsonLd(
    'compare',
    ready && [
      breadcrumbSchema([
        { name: 'Ghost Cable', path: '/' },
        { name: 'Compare plans', path: PATH },
      ]),
      ...plans.map((p) =>
        productSchema({
          name: `${PLANS[p].name} IPTV plan`,
          description: `${PLANS[p].tagline}. ${PLANS[p].blurb}`,
          path: `/p/${p.toLowerCase()}`,
          prices: planPriceList(p),
        }),
      ),
    ],
  );

  return (
    <div className={cn(WRAP, 'pb-24 pt-16 md:pt-24')}>
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">
          {t(P.home)}
        </Link>
        <span className="mx-2" aria-hidden="true">
          /
        </span>
        <span aria-current="page">{t('Compare plans')}</span>
      </nav>

      <p className={cn(LABEL, 'mt-8')}>{t('Compare plans')}</p>
      <h1 className="mt-4 text-[clamp(44px,8vw,96px)] font-light leading-[0.98]">
        <span className={cn('font-heading', GLOW)}>{PLANS.Wraith.name}</span>
        <span className="text-muted-foreground"> {t(P.vs)} </span>
        <span className={cn('font-heading', GLOW)}>{PLANS.Demon.name}</span>
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-foreground/85">
        {t(P.compareIntro)}
      </p>

      {!ready ? (
        <div aria-busy="true" className="min-h-[60vh]" />
      ) : !plans.length ? (
        <div className="mt-16 border-t border-border pt-10">
          <p className="text-[15px] text-muted-foreground">{t(P.notOnSale)}</p>
          <Button size="lg" className="mt-6" asChild>
            <Link to="/">{t(P.back)}</Link>
          </Button>
        </div>
      ) : (
        <>
          {both && (
            <div className="mt-14">
              <PlanQuiz />
            </div>
          )}

          {both && (
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {KEYS.map((p) => (
                <div key={p} className="rounded-lg border border-border bg-card p-6">
                  <p className={LABEL}>{t(P.pickIf, { name: PLANS[p].name })}</p>
                  <ul className="mt-4 space-y-2.5 text-[15px] text-foreground/85">
                    {PICK[p].map((line) => (
                      <li key={line} className="flex items-start gap-3">
                        <ApperIcon name="Check" size={16} className="mt-1 shrink-0 text-primary" />
                        {t(line)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="mt-16">
            <PlanCompare plans={plans} conns={conns} setConns={setConns} />
          </div>
        </>
      )}

      <p className="mt-16 border-t border-border pt-8 text-sm text-muted-foreground">
        {t(P.notSure)}{' '}
        <Link to={{ pathname: '/', hash: '#faq' }} className="text-primary underline-offset-4 hover:underline">
          {t(P.readFaq)}
        </Link>{' '}
        {t(P.or)}{' '}
        <Link to={{ pathname: '/', hash: '#contact' }} className="text-primary underline-offset-4 hover:underline">
          {t(P.messageUs)}
        </Link>
        .
      </p>
    </div>
  );
}
